import { withPlugins, domEvents, scroll } from '@weareduet/plugins';
import { loadGsap } from '@/utils/gsap';
import domify from 'domify';

const CARD_SELECTOR = '[data-card-hover]';
const DEFAULT_TEXT = 'VIEW';

async function CardHoverPill({ plugins: { addEvents, scroll } }) {
	// Bail on touch-only devices. `any-pointer` (not `pointer`) so that
	// touchscreen laptops — where the primary pointer reports as coarse —
	// still get the pill from their trackpad. Runtime pointerType guards
	// keep actual touch input out.
	if (!window.matchMedia('(any-pointer: fine)').matches) return;

	let gsapRef = null;
	try {
		const { gsap } = await loadGsap();
		gsapRef = gsap;

		let latestText = DEFAULT_TEXT;
		let activeCard = null;
		let hasPositioned = false;

		// Last known cursor position, tracked whether or not a card is
		// hovered — scroll re-syncing needs it to hit-test the page.
		const pointerPos = { x: null, y: null };

		// Create marker element once
		const marker = domify(
			`<span class="fixed z-[48] top-0 left-0 inline-block pointer-events-none"><span class="btn btn--card-hover">${latestText}</span></span>`
		);
		document.body.appendChild(marker);

		const xTo = gsapRef.quickTo(marker, 'x', {
			duration: 0.2,
			ease: 'power2.out',
		});
		const yTo = gsapRef.quickTo(marker, 'y', {
			duration: 0.2,
			ease: 'power2.out',
		});

		// quickTo() runs on GSAP's own ticker — only call it when the
		// target changes (on pointer move), never per-frame. The pill
		// intentionally tracks from the cursor (top-left), not centred.
		// The first position is snapped so the pill never flies in from 0,0.
		function moveMarker(x, y) {
			if (hasPositioned) {
				xTo(x);
				yTo(y);
			} else {
				xTo(x, x);
				yTo(y, y);
				hasPositioned = true;
			}
		}

		function isMouse({ pointerType }) {
			// Trackpads report as `mouse`; only pen/touch are excluded.
			return !pointerType || pointerType === 'mouse';
		}

		function enterCard(card, event) {
			if (card === activeCard) return;
			if (activeCard) leaveCard();

			activeCard = card;

			// Suppress the native title tooltip while our custom pill is
			// active, otherwise both tooltips can show at once.
			if (card.hasAttribute('title')) {
				card.dataset.titleCache = card.getAttribute('title');
				card.removeAttribute('title');
			}

			const text = card.dataset.title || DEFAULT_TEXT;
			if (latestText !== text) {
				latestText = text;
				marker.innerHTML = `<span class="btn btn--card-hover">${latestText}</span>`;
			}

			if (event) moveMarker(event.clientX, event.clientY);

			marker.classList.add('show');
			document.documentElement.classList.add('has-card-hover');
		}

		function leaveCard() {
			if (activeCard && activeCard.dataset.titleCache) {
				activeCard.setAttribute('title', activeCard.dataset.titleCache);
				delete activeCard.dataset.titleCache;
			}
			activeCard = null;

			marker.classList.remove('show');
			document.documentElement.classList.remove('has-card-hover');
		}

		// Re-derive the hovered card from the cursor position. A trackpad
		// two-finger scroll moves the page under a stationary cursor, so no
		// pointerover/pointerout ever fires — without this the pill goes
		// stale and the native title tooltip reappears mid-hover.
		function syncFromPointer() {
			if (pointerPos.x === null) return;

			const element = document.elementFromPoint(pointerPos.x, pointerPos.y);
			const card = element ? element.closest(CARD_SELECTOR) : null;

			if (card) {
				enterCard(card);
			} else if (activeCard) {
				leaveCard();
			}
		}

		function onPointerOver(event) {
			if (!isMouse(event)) return;

			// The delegated target may be a child of the card.
			const card = event.target.closest
				? event.target.closest(CARD_SELECTOR)
				: null;
			if (card) enterCard(card, event);
		}

		function onPointerOut({ relatedTarget, pointerType }) {
			if (!isMouse({ pointerType })) return;
			// Ignore moves between the card and its own children.
			if (activeCard && relatedTarget && activeCard.contains(relatedTarget)) {
				return;
			}

			leaveCard();
		}

		function onPointerMove(event) {
			if (!isMouse(event)) return;

			pointerPos.x = event.clientX;
			pointerPos.y = event.clientY;

			// The active card can vanish under the cursor on a Barba
			// transition — drop the pill rather than leave it stranded.
			if (activeCard && !activeCard.isConnected) leaveCard();
			if (!activeCard) return;

			moveMarker(event.clientX, event.clientY);
		}

		scroll.progress(syncFromPointer);
		scroll.stop(syncFromPointer);

		addEvents(document.body, {
			pointermove: onPointerMove,
			[`pointerover ${CARD_SELECTOR}`]: onPointerOver,
			[`pointerout ${CARD_SELECTOR}`]: onPointerOut,
		});
	} catch (error) {
		console.error('CardHoverPill: Failed to load dependencies', error);
		return () => {};
	}
}

export default withPlugins(domEvents, scroll)(CardHoverPill);
